/**
 * facets.js
 * Filter und Sortierung auf Kategorieseiten.
 *
 * Die Tag-Filter (Größe, Pflege, Eigenschaften) stecken im Pfad der URL
 * (/collections/zimmerpflanzen/pflegeleicht), die Filter aus der App
 * Search & Discovery und die Sortierung als Query-Parameter. Beides wird
 * hier zusammengesetzt, über die Section Rendering API neu gerendert und
 * per History API in die Adresszeile geschrieben. Getauscht werden nur
 * #CollectionFacets und #CollectionProducts.
 *
 * Ohne JavaScript funktioniert alles klassisch: Tag-Filter sind normale
 * Links, für die übrigen Filter gibt es den Anwenden-Button.
 */

class FacetFiltersForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.defaultSort = this.dataset.defaultSort;
    this.cache = new Map();
    this.pendingController = null;
    this.panelOpen = false;

    // Preisfelder erst nach Tipppause anwenden, Auswahl und Sortierung sofort.
    this.debouncedApply = theme.utils.debounce(() => this.applyFilters(), 600);

    this.addEventListener('change', (event) => {
      const input = event.target;
      if (input.name === 'sort_by' || input.type === 'checkbox') this.applyFilters();
    });

    this.addEventListener('input', (event) => {
      if (event.target.type === 'number') this.debouncedApply();
    });

    this.addEventListener('submit', (event) => {
      event.preventDefault();
      this.applyFilters();
    });

    // Auf document, damit auch der Zurücksetzen-Link im leeren
    // Produktbereich außerhalb dieses Elements greift.
    this.onDocumentClick = (event) => {
      const toggle = event.target.closest('[data-filter-toggle]');
      if (toggle) {
        event.preventDefault();
        this.togglePanel();
        return;
      }

      const link = event.target.closest('a[data-filter-link]');
      if (link) {
        event.preventDefault();
        this.renderPage(link.href, true);
      }
    };
    document.addEventListener('click', this.onDocumentClick);

    this.onKeyup = (event) => {
      if (event.key === 'Escape' && this.panelOpen) this.togglePanel(false);
    };
    document.addEventListener('keyup', this.onKeyup);

    this.onPopstate = () => {
      this.renderPage(window.location.href, false);
    };
    window.addEventListener('popstate', this.onPopstate);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onKeyup);
    window.removeEventListener('popstate', this.onPopstate);
    document.body.classList.remove('filters-open');
  }

  get panel() {
    return this.querySelector('[data-filter-panel]');
  }

  /* Mobiles Filterpanel auf und zu. Am Desktop steht die Spalte fest,
     dort ist der Umschalter ausgeblendet. */
  togglePanel(force) {
    const panel = this.panel;
    if (!panel) return;

    this.panelOpen = typeof force === 'boolean' ? force : !this.panelOpen;
    panel.classList.toggle('is-open', this.panelOpen);
    document.body.classList.toggle('filters-open', this.panelOpen);

    const button = this.querySelector('[data-filter-toggle][aria-expanded]');
    button?.setAttribute('aria-expanded', String(this.panelOpen));

    if (this.panelOpen) {
      panel.querySelector('.collection-filters__close')?.focus({ preventScroll: true });
    } else {
      button?.focus({ preventScroll: true });
    }
  }

  /* Aktuelle Auswahl als URL: Pfad trägt die Tag-Filter, Query die
     Storefront-Filter und die Sortierung. */
  buildUrl() {
    const form = this.querySelector('#FacetFiltersForm');
    const params = new URLSearchParams();

    if (form) {
      for (const [key, value] of new FormData(form).entries()) {
        if (typeof value !== 'string' || value.trim() === '') continue;
        params.append(key, value);
      }
    }

    const sort = this.querySelector('#SortBy');
    // Standard-Sortierung nicht in die URL schreiben, hält Links sauber.
    if (sort && sort.value && sort.value !== this.defaultSort) {
      params.append('sort_by', sort.value);
    }

    const path = form
      ? new URL(form.action, window.location.origin).pathname
      : window.location.pathname;
    const search = params.toString();

    return search ? `${path}?${search}` : path;
  }

  applyFilters() {
    this.renderPage(this.buildUrl(), true);
  }

  async renderPage(href, updateHistory) {
    const target = new URL(href, window.location.origin);

    this.pendingController?.abort();
    const controller = new AbortController();
    this.pendingController = controller;

    const products = document.getElementById('CollectionProducts');
    products?.classList.add('is-loading');
    products?.setAttribute('aria-busy', 'true');

    const query = target.search ? `&${target.search.slice(1)}` : '';
    const url = `${target.pathname}?section_id=${this.sectionId}${query}`;

    try {
      let html = this.cache.get(url);
      if (!html) {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        html = await response.text();
        this.cache.set(url, html);
        if (this.cache.size > 20) {
          this.cache.delete(this.cache.keys().next().value);
        }
      }

      if (controller.signal.aborted) return;
      this.renderSection(html);

      if (updateHistory) {
        history.pushState({}, '', `${target.pathname}${target.search}`);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fallback: klassische Navigation, damit die Filterung trotzdem greift.
      window.location.href = href;
    } finally {
      if (this.pendingController === controller) {
        this.pendingController = null;
        const el = document.getElementById('CollectionProducts');
        el?.classList.remove('is-loading');
        el?.removeAttribute('aria-busy');
      }
    }
  }

  renderSection(html) {
    // Fokus und aktuellen Eingabewert vor dem Tausch merken.
    const active = document.activeElement;
    const activeId = active?.id;
    const activeValue = active?.type === 'number' ? active.value : null;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    ['CollectionFacets', 'CollectionProducts'].forEach((id) => {
      const target = document.getElementById(id);
      const source = doc.getElementById(id);
      if (target && source) target.innerHTML = source.innerHTML;
    });

    // Offenes Panel bleibt offen, der Umschalter kommt frisch aus dem HTML.
    if (this.panelOpen) {
      this.panel?.classList.add('is-open');
      this.querySelector('[data-filter-toggle][aria-expanded]')?.setAttribute('aria-expanded', 'true');
    }

    if (activeId) {
      const el = document.getElementById(activeId);
      if (el) {
        if (activeValue !== null) el.value = activeValue;
        el.focus({ preventScroll: true });
      }
    }
  }
}

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
